import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  areDateRangesEqual,
  buildLegacySelectedFilters,
  getEffectiveSlicerPanel,
  resolveInitialDashboardDateRange,
} from '../runtime'
import type { DashboardDefinition, DashboardRuntimeState, DatePresetOption } from '../types'
import type { useDashboardView } from './engine/useDashboardView'

export type DashboardViewEngine = ReturnType<typeof useDashboardView>

/** What the filters currently mean, in the shapes exports and the chat panel take. */
export function useDashboardRuntime(engine: DashboardViewEngine) {
  const runtimeState = useMemo<DashboardRuntimeState>(() => ({
    slicerValues: { ...engine.appliedFilters.slicerValues },
    dateRange: { ...engine.appliedFilters.dateRange },
    widgetStateById: Object.fromEntries(
      Object.entries(engine.widgetSessions).map(([widgetId, widgetSession]) => [
        widgetId,
        {
          groupByDimension: widgetSession.query.groupByDimension,
          groupByDimensions: widgetSession.query.groupByDimensions,
        },
      ]),
    ),
  }), [engine.appliedFilters, engine.widgetSessions])
  const selectedFilters = useMemo(
    () => buildLegacySelectedFilters(engine.dashboard, runtimeState),
    [engine.dashboard, runtimeState],
  )
  return { runtimeState, selectedFilters }
}

/**
 * Auto-apply, and settling an older dashboard's default date once the model's
 * presets are known. `datePresetOptions` is undefined while they load.
 */
export function useDashboardFilterBehaviour(engine: DashboardViewEngine, datePresetOptions: DatePresetOption[] | undefined) {
  const dashboard = engine.dashboard
  const [autoApply, setAutoApply] = useState<boolean>(true)
  const autoApplyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    const slicerPanel = getEffectiveSlicerPanel(dashboard)
    setAutoApply(typeof slicerPanel?.autoApply === 'boolean' ? slicerPanel.autoApply : true)
  }, [dashboard])

  const applyGlobalFiltersRef = useRef(engine.actions.applyGlobalFilters)
  applyGlobalFiltersRef.current = engine.actions.applyGlobalFilters
  useEffect(() => {
    if (!autoApply) return
    if (!engine.hasPendingGlobalFilterChanges) return

    if (autoApplyTimerRef.current) clearTimeout(autoApplyTimerRef.current)
    autoApplyTimerRef.current = setTimeout(() => {
      void applyGlobalFiltersRef.current()
    }, 500)

    return () => {
      if (autoApplyTimerRef.current) {
        clearTimeout(autoApplyTimerRef.current)
        autoApplyTimerRef.current = null
      }
    }
  }, [autoApply, engine.hasPendingGlobalFilterChanges, engine.draftFilters])

  useEffect(() => () => {
    if (autoApplyTimerRef.current) {
      clearTimeout(autoApplyTimerRef.current)
      autoApplyTimerRef.current = null
    }
  }, [])

  // The engine resolves the date range before the catalog loads: an older dashboard
  // (no dateRange.mode) starts on the custom range and a stored preset is provisional.
  // Once the catalog settles, re-resolve once per loaded dashboard: if the user has
  // not touched the date control yet, apply the catalog's default, or the custom
  // range for a stored preset the catalog does not list (or a failed catalog).
  // Explicit custom/none modes resolve identically and are never changed.
  const dateDefaultsReconciledForRef = useRef<DashboardDefinition | null>(null)
  const draftDateRange = engine.draftFilters.dateRange
  useEffect(() => {
    if (!dashboard || !datePresetOptions) return
    if (dateDefaultsReconciledForRef.current === dashboard) return
    dateDefaultsReconciledForRef.current = dashboard

    const slicerPanel = getEffectiveSlicerPanel(dashboard)
    if (!slicerPanel?.dateRange) return
    const provisional = resolveInitialDashboardDateRange(slicerPanel.dateRange)
    const resolved = resolveInitialDashboardDateRange(slicerPanel.dateRange, datePresetOptions)
    if (areDateRangesEqual(provisional, resolved)) return
    if (!areDateRangesEqual(draftDateRange, provisional)) return

    engine.actions.changeDraftDateRange(resolved)
    void engine.actions.applyGlobalFilters()
  }, [datePresetOptions, dashboard, draftDateRange, engine.actions])

  const clearAll = useCallback(() => {
    engine.actions.resetDraftFilters()
  }, [engine.actions])

  return { autoApply, setAutoApply, clearAll }
}
